/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandRemessaEmAndamento } from "@/store-zustand/cliente/remessas-em-andamento";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { handlerGetRemessaEmAndamento } from "@/domain/use-cases/remessas/buscar-remessa-em-andamento";
import { getStatusLabel } from "@/utils/get-status-label";

export interface ICliente {
  id: number;
  nome: string;
  email: string;
  cnpj: string;
  cpf: string;
  ativo: boolean;
  tipo_pessoa: string;
  telefone: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  foto_frente_url: string | null;
  foto_verso_url: string | null;
}

interface ISolicitante {
  id: number;
  remessa_id: number;
  user_id: number;
  nome: string;
  cpf: string;
  telefone: string;
  created_at: string;
  updated_at: string;
}

interface IConsultor {
  id: number;
  cliente_nome: string;
  email: string;
  cpf: string;
  telefone: string;
  created_at: string;
  updated_at: string;
}
export interface ITecnologia {
  id: number;
  nome: string;
  descricao: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface IModeloTecnico {
  id: number;
  user_id: number;
  produto_id: number;
  tecnologia_id: number;
  nome_modelo: string;
  tipo_entrega: string;
  posicionamento: string;
  tem_furo: number;
  tem_carga_foto: number;
  tem_dados_variaveis: number;
  campo_chave: string;
  foto_frente_path: string | null;
  foto_verso_path: string | null;
  observacoes: string;
  created_at: string;
  updated_at: string;
  foto_frente_url: string;
  foto_verso_url: string;
}

export interface IRemessa {
  id: number;
  numero_remessa: string;
  total_solicitacoes: number;
  situacao: string;
  data_fim_producao: string;
  data_inicio_producao: string;
  posicao: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  cliente: ICliente;
  tecnologia: ITecnologia;
  modelo_tecnico: IModeloTecnico;
  solicitante: ISolicitante;
  consultor?: IConsultor; // opcional, você usa no map
  responsavel?: string; // idem
  designer: any;
  observacao: string | null;
  historico_status_remessa: any;
  items_remessa: {
    id: number;
    remessa_id: number;
    cliente_id: number;
    nome: string;
    matricula: string;
    file_path: string;
    created_at: Date;
    updated_at: Date;
    url: string;
  }[];
}

export interface IMinhasTarefasResponse {
  code: number;
  status: string;
  message: string;
  data: IRemessa[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

function mapRemessasEmAndamentoToTable(data: IRemessa[]): Record<string, any>[] {
  return data?.map((remessa) => ({
    remessa: remessa.id,
    numero_remessa: remessa.numero_remessa ?? "N/A",
    nome_identificacao: remessa.cliente?.nome ?? "N/A",
    quantidadeSolicitacao: remessa.total_solicitacoes ?? 0,
    status: getStatusLabel(remessa.situacao) ?? "N/A",
    data: remessa.created_at ? new Date(remessa.created_at).toLocaleString("pt-BR") : "N/A",
    data_inicio_producao: remessa.data_inicio_producao
      ? new Date(remessa.data_inicio_producao).toLocaleString("pt-BR")
      : "N/A",
    data_fim_producao: remessa.data_fim_producao
      ? new Date(remessa.data_fim_producao).toLocaleString("pt-BR")
      : "N/A",
    prazo: remessa.data_inicio_producao
      ? new Date(remessa.data_inicio_producao).toLocaleString("pt-BR")
      : "N/A",
    posicao: remessa.posicao ?? "N/A",
    tecnologia: remessa.tecnologia?.nome ?? "N/A",
    modelo: remessa.modelo_tecnico ?? "N/A",
    nome_modelo: remessa.modelo_tecnico?.nome_modelo ?? "N/A",
    modelo_foto_frente_url: remessa.modelo_tecnico?.foto_frente_url ?? null,
    modelo_foto_verso_url: remessa.modelo_tecnico?.foto_verso_url ?? null,
    solicitante: remessa.solicitante?.nome ?? "N/A",
    designer: remessa?.designer?.nome ?? "N/A",
    observacao: remessa?.observacao ?? "",
    hasMensagem: Boolean(remessa?.observacao !== null),
    historico_status_remessa: remessa?.historico_status_remessa,
    items_remessa: remessa?.items_remessa ?? [],
    foto_frente_url: remessa?.cliente?.foto_frente_url ?? "N/A",
    foto_verso_url: remessa?.cliente?.foto_verso_url ?? "N/A",
  }));
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

function useRemessasEmAndamento() {
  const { currentPage, itemsPerPage, searchItem, setPaginations, setLastPage } =
    useStoreZustandRemessaEmAndamento();

  const { token: accessToken, user } = useStoreZustandUserAuth();

  // Chave de cache única baseada na página atual e na quantidade de itens por página
  const queryKey = ["remessas-em-andamento", currentPage, itemsPerPage, searchItem];

  const {
    isLoading,
    error: isError,
    data: remessasResponse,
  } = useQuery<IMinhasTarefasResponse>({
    queryKey,
    queryFn: async () => {
      const idParams = user?.tipo_login === "subordinado" ? user?.cliente_principal?.id : user?.id;

      const remessasResponse = await handlerGetRemessaEmAndamento(
        accessToken!,
        idParams!,
        currentPage,
        itemsPerPage,
        searchItem!
      );

      return remessasResponse;
    },
    //diz ao React Query: "enquanto os novos dados estão carregando, mantenha os dados antigos"
    placeholderData: (previousData) => previousData,
  });

  const remessasEmAndamento = mapRemessasEmAndamentoToTable(remessasResponse?.data as IRemessa[]);

  const pagination = remessasResponse?.pagination;

  // só recalcula se currentPage ou last_page mudar
  const pages = useMemo(() => {
    return generatePagination(currentPage, pagination?.last_page ?? 1);
  }, [currentPage, pagination?.last_page]);

  //  atualiza zustand uma única vez
  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(pagination?.last_page);
  }, [pages, setPaginations, setLastPage, pagination]);

  return {
    remessasEmAndamento: remessasEmAndamento,
    paginations: pages,
    isLoading,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useRemessasEmAndamento };
